import React, { useEffect, useState } from 'react';
import { useDatabase } from '../../../db/DatabaseProvider';
import { DollarSign, ArrowUpRight, ArrowDownRight } from 'lucide-react';
import type { TransactionDocType } from '../../../db/schemas';
import { FormatCurrency } from '../../common/FormatCurrency';

interface Props {
  customerId: string;
}

export function CustomerTabTransactions({ customerId }: Props) {
  const db = useDatabase();
  const [transactions, setTransactions] = useState<TransactionDocType[]>([]);

  useEffect(() => {
    if (!db || !customerId) return;
    const sub = db.transactions.find({ selector: { customer_id: customerId, is_deleted: false } }).$.subscribe(docs => {
      const sorted = [...docs.map(d => d.toJSON())].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
      setTransactions(sorted);
    });
    return () => sub.unsubscribe();
  }, [db, customerId]);

  return (
    <div className="space-y-4 animate-fade-in">
      <h3 className="text-lg font-bold text-text-primary">Transacciones</h3>
      {transactions.length === 0 ? (
        <div className="text-center p-8 bg-surface border border-dashed border-border rounded-xl">
          <DollarSign className="w-8 h-8 text-text-tertiary mx-auto mb-2" />
          <p className="text-sm text-text-secondary">No hay transactions registradas.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {transactions.map((tx) => (
            <div key={tx.id} className="bg-surface border border-border rounded-xl p-4 flex flex-col sm:flex-row justify-between sm:items-center gap-3">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-full ${tx.type === 'income' ? 'bg-emerald-500/10 text-emerald-500' : 'bg-red-500/10 text-red-500'}`}>
                  {tx.type === 'income' ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
                </div>
                <div>
                  <div className="font-bold text-sm text-text-primary">{tx.description || tx.subtype}</div>
                  <div className="text-xs text-text-secondary mt-0.5">{new Date(tx.date).toLocaleString('es-ES')}</div>
                </div>
              </div>
              <div className="text-right">
                <div className={`font-bold text-base ${tx.type === 'income' ? 'text-emerald-500' : 'text-red-500'}`}>
                  {tx.type === 'income' ? '+' : '-'}
                  <FormatCurrency amount={tx.total_amount} currency={tx.base_currency} />
                </div>
                <div className="text-xs font-semibold text-text-secondary uppercase tracking-wide">
                  {tx.payment_status}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
